import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, ExternalLink, AlertTriangle, AlertOctagon, Info, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import api from "../lib/api";

const POLL_MS = 30_000;

function SeverityIcon({ severity, className = "w-4 h-4" }) {
  if (severity === "critical") return <AlertOctagon className={`${className} text-red-600`} strokeWidth={1.7} />;
  if (severity === "warning") return <AlertTriangle className={`${className} text-amber-500`} strokeWidth={1.7} />;
  return <Info className={`${className} text-zinc-500`} strokeWidth={1.7} />;
}

function relativeTime(iso) {
  if (!iso) return "";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es });
  } catch {
    return "";
  }
}

export default function NotificationBell({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data?.count || 0);
    } catch { /* ignore */ }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 15 } });
      setItems(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadCount();
    intervalRef.current = setInterval(loadCount, POLL_MS);
    return () => clearInterval(intervalRef.current);
  }, [loadCount]);

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  const handleMarkAll = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      await Promise.all([loadCount(), loadItems()]);
    } catch { /* ignore */ }
  };

  const handleItemClick = async (n) => {
    try {
      if (!n.read) {
        await api.post(`/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setCount((c) => Math.max(0, c - 1));
      }
    } catch { /* ignore */ }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${compact ? "h-9 w-9" : "h-10 w-10"} text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100`}
          data-testid="notifications-bell"
          aria-label="Notificaciones"
        >
          <Bell className="w-[18px] h-[18px]" strokeWidth={1.6} />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center leading-none border-2 border-white"
              data-testid="notifications-bell-badge"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[360px] sm:w-[400px] p-0 border-zinc-200 shadow-xl"
        data-testid="notifications-popover"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 tracking-tight">Notificaciones</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {count > 0 ? `${count} sin leer` : "Todo al día"}
            </p>
          </div>
          {count > 0 && (
            <button
              onClick={handleMarkAll}
              className="text-[11px] font-medium text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
              data-testid="notifications-mark-all"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Marcar todo
            </button>
          )}
        </div>
        <Separator />
        <ScrollArea className="max-h-[440px]">
          {loading && items.length === 0 ? (
            <div className="py-10 flex items-center justify-center text-xs text-zinc-400">
              Cargando…
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 px-6 text-center" data-testid="notifications-empty">
              <Bell className="w-8 h-8 text-zinc-300 mx-auto mb-3" strokeWidth={1.4} />
              <p className="text-sm font-medium text-zinc-700">Sin notificaciones</p>
              <p className="text-xs text-zinc-500 mt-1">Aquí verá alertas del sistema y kioskos sin conexión.</p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(n)}
                    className={`w-full text-left px-4 py-3 flex gap-3 items-start hover:bg-zinc-50 transition-colors ${n.read ? "" : "bg-zinc-50/40"}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="mt-0.5 shrink-0">
                      <SeverityIcon severity={n.severity} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm leading-tight truncate ${n.read ? "text-zinc-700 font-medium" : "text-zinc-900 font-semibold"}`}>
                          {n.title}
                        </p>
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />}
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <p className="text-[10px] uppercase tracking-wide text-zinc-400">{relativeTime(n.created_at)}</p>
                        {n.link && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-500">
                            <ExternalLink className="w-2.5 h-2.5" /> Abrir
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
        <Separator />
        <div className="p-2">
          <Link
            to="/notificaciones"
            onClick={() => setOpen(false)}
            className="block w-full text-center text-xs font-medium text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50 py-2 rounded-md"
            data-testid="notifications-view-all"
          >
            Ver todas las notificaciones
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
